/**
 * SalesModal Component
 *
 * Modal for creating service sales and budgets following the UnifiedTransactionForm pattern.
 * Single-page layout with card-based sections, no sidebar/tabs.
 *
 * Uses transaction-modal-responsive class for exact sizing:
 * - Mobile (<640px): 100vw × 100vh (full screen)
 * - Tablet (640-1023px): 95vw × 95vh
 * - Desktop (≥1024px): 90vw × 90vh, max-width: 1728px
 */

import React, { useState, useId, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ShoppingCart, FileText, Plus, Trash2, Save, User, Calendar, Building, Archive } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useClients, useIndustryServicesForSales, useCostCentersForSales, useSalesTeam, useCreateServiceSale, useUpdateServiceSale } from '@/hooks/useServiceSales';
import { toast } from 'sonner';
import { NewClientDialog } from '@/pages/services/sales/components/NewClientDialog';
import { cn } from '@/lib/utils';
import type { ServiceSaleItem } from '@/services/sales';

const serviceSaleSchema = z.object({
  type: z.enum(['sale', 'budget']),
  client_id: z.string().min(1, 'Cliente é obrigatório'),
  sale_date: z.string().min(1, 'Data é obrigatória'),
  expiry_date: z.string().optional(),
  cost_center_id: z.string().optional(),
  salesperson_id: z.string().optional(),
  notes: z.string().optional(),
  terms_conditions: z.string().optional(),
  client_notes: z.string().optional(),
  internal_notes: z.string().optional(),
});

const itemSchema = z.object({
  description: z.string().min(1, 'Descrição é obrigatória'),
  quantity: z.number().min(0.01, 'Quantidade deve ser maior que zero'),
  unit: z.string().default('un'),
  unitPrice: z.number().min(0, 'Preço unitário é obrigatório'),
  discount_type: z.enum(['percentage', 'fixed']).default('percentage'),
  discount_value: z.number().default(0),
  tax_percentage: z.number().default(0),
  notes: z.string().optional(),
});

interface SalesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'sale' | 'budget' | 'auto';
  initialData?: Partial<z.infer<typeof serviceSaleSchema>>;
  onSuccess?: (saleId: string) => void;
  saleId?: string | null;
}

type SaleItem = z.infer<typeof itemSchema> & { id: string };

export function SalesModal({ open, onOpenChange, mode = 'auto', initialData, onSuccess, saleId }: SalesModalProps) {
  const { user, profile } = useAuth();
  const { data: clients = [] } = useClients();
  const { data: services = [] } = useIndustryServicesForSales();
  const { data: costCenters = [] } = useCostCentersForSales();
  const { data: salesTeam = [] } = useSalesTeam();
  const createSale = useCreateServiceSale();
  const updateSale = useUpdateServiceSale();

  // Determine if we're in edit mode
  const isEditMode = !!saleId;

  const rawFormId = useId();
  const formId = useMemo(
    () => `sales-form-${rawFormId.replace(/:/g, "")}`,
    [rawFormId]
  );

  const [items, setItems] = useState<SaleItem[]>([]);
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<z.infer<typeof serviceSaleSchema>>({
    resolver: zodResolver(serviceSaleSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      type: mode === 'auto' ? 'sale' : mode,
      client_id: initialData?.client_id || '',
      sale_date: initialData?.sale_date || new Date().toISOString().split('T')[0],
      expiry_date: initialData?.expiry_date || '',
      cost_center_id: initialData?.cost_center_id || '',
      salesperson_id: initialData?.salesperson_id || '',
      notes: initialData?.notes || '',
      terms_conditions: initialData?.terms_conditions || '',
      client_notes: initialData?.client_notes || '',
      internal_notes: initialData?.internal_notes || '',
    },
  });

  const formData = form.watch();
  const formType = formData.type;

  const addItem = () => {
    const newItem: SaleItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unit: 'un',
      unitPrice: 0,
      discount_type: 'percentage',
      discount_value: 0,
      tax_percentage: 0,
      notes: '',
    };
    setItems([...items, newItem]);
  };

  const updateItem = (id: string, field: keyof SaleItem, value: any) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const calculateItemTotal = (item: SaleItem) => {
    const subtotal = item.quantity * item.unitPrice;
    const discount = item.discount_type === 'percentage'
      ? subtotal * (item.discount_value / 100)
      : item.discount_value;
    const taxable = subtotal - discount;
    const tax = taxable * (item.tax_percentage / 100);
    return taxable + tax;
  };

  const calculateSubtotal = () => items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const calculateTotalDiscount = () => items.reduce((sum, item) => {
    const subtotal = item.quantity * item.unitPrice;
    return sum + (item.discount_type === 'percentage'
      ? subtotal * (item.discount_value / 100)
      : item.discount_value);
  }, 0);
  const calculateTotalTax = () => items.reduce((sum, item) => {
    const subtotal = item.quantity * item.unitPrice;
    const discount = item.discount_type === 'percentage'
      ? subtotal * (item.discount_value / 100)
      : item.discount_value;
    return sum + ((subtotal - discount) * (item.tax_percentage / 100));
  }, 0);
  const calculateTotal = () => calculateSubtotal() - calculateTotalDiscount() + calculateTotalTax();

  const onSubmit = async (data: z.infer<typeof serviceSaleSchema>) => {
    if (!user?.id || !profile?.company_id) {
      toast.error('Usuário não autenticado');
      return;
    }

    if (items.length === 0 && !isEditMode) {
      toast.error('Adicione pelo menos um item');
      return;
    }

    setIsSaving(true);
    const loadingMessage = isEditMode ? 'Salvando alterações...' : 'Criando venda...';
    toast.loading(loadingMessage, { id: 'sales-saving' });

    try {
      if (isEditMode && saleId) {
        // Update existing sale
        await updateSale.mutateAsync({
          id: saleId,
          updates: {
            clientId: data.client_id,
            saleDate: data.sale_date,
            issueDate: data.sale_date,
            expiryDate: data.expiry_date || undefined,
            costCenterId: data.cost_center_id ? parseInt(data.cost_center_id) : undefined,
            salespersonId: data.salesperson_id || undefined,
            notes: data.notes || undefined,
            termsConditions: data.terms_conditions || undefined,
            clientNotes: data.client_notes || undefined,
            internalNotes: data.internal_notes || undefined,
          },
        });

        toast.success(`${data.type === 'budget' ? 'Orçamento' : 'Venda'} atualizado com sucesso!`, { id: 'sales-saving' });
        onSuccess?.(saleId);
      } else {
        // Create new sale
        const result = await createSale.mutateAsync({
          type: data.type,
          clientId: data.client_id,
          companyId: profile.company_id,
          userId: user.id,
          saleDate: data.sale_date,
          expiryDate: data.expiry_date || undefined,
          costCenterId: data.cost_center_id ? parseInt(data.cost_center_id) : undefined,
          salespersonId: data.salesperson_id || undefined,
          notes: data.notes || undefined,
          termsConditions: data.terms_conditions || undefined,
          clientNotes: data.client_notes || undefined,
          internalNotes: data.internal_notes || undefined,
          items: items.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unitPrice,
            unit: item.unit,
            discount_type: item.discount_type,
            discount_value: item.discount_value,
            tax_percentage: item.tax_percentage,
            notes: item.notes,
          })),
        });

        toast.success(`${data.type === 'budget' ? 'Orçamento' : 'Venda'} criado com sucesso!`, { id: 'sales-saving' });
        onSuccess?.(result.id);
      }
      onOpenChange(false);

      // Reset form and items after modal closes
      setTimeout(() => {
        form.reset();
        setItems([]);
      }, 100);
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} sale:`, error);
      const errorMessage = isEditMode
        ? 'Erro ao atualizar venda. Tente novamente.'
        : 'Erro ao criar venda. Tente novamente.';
      toast.error(errorMessage, { id: 'sales-saving' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClientCreated = (client: any) => {
    form.setValue('client_id', client.id);
    setShowNewClientDialog(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(open) => !open && onOpenChange(false)}>
        <DialogContent className="transaction-modal-responsive p-0">
          <Form {...form}>
            <div className="flex flex-col h-full bg-background">
              {/* Header */}
              <div className="px-8 py-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="grid grid-cols-3 items-center">
                  {/* Left Section - Icon and Title */}
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "p-3 rounded-xl shadow-lg",
                      formType === 'sale'
                        ? "bg-gradient-to-br from-green-500 to-green-600 text-white"
                        : "bg-gradient-to-br from-blue-500 to-blue-600 text-white"
                    )}>
                      {formType === 'sale' ? <ShoppingCart className="h-8 w-8" /> : <FileText className="h-8 w-8" />}
                    </div>
                    <div>
                      <DialogTitle className="text-3xl font-bold text-foreground">
                        {isEditMode
                          ? (formType === 'sale' ? 'Editar Venda' : 'Editar Orçamento')
                          : (formType === 'sale' ? 'Nova Venda' : 'Novo Orçamento')
                        }
                      </DialogTitle>
                      <DialogDescription className="text-lg text-muted-foreground mt-1">
                        {isEditMode
                          ? `Altere os dados do ${formType === 'sale' ? 'venda' : 'orçamento'}`
                          : `Preencha os dados para ${formType === 'sale' ? 'criar uma venda' : 'criar um orçamento'}`
                        }
                      </DialogDescription>
                    </div>
                  </div>

                  {/* Center Section - Type Selector */}
                  <div className="justify-center">
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="grid grid-cols-2 gap-4">
                              <Button
                                type="button"
                                variant={field.value === 'sale' ? "default" : "outline"}
                                className={cn(
                                  "h-12 text-base font-semibold rounded-lg transition-all duration-200 border-2",
                                  field.value === 'sale'
                                    ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-green-500 shadow-md"
                                    : "hover:border-green-500 hover:text-green-600 hover:bg-green-50/10 border-border"
                                )}
                                onClick={() => field.onChange('sale')}
                              >
                                <ShoppingCart className="mr-2 h-5 w-5" />
                                Venda
                              </Button>
                              <Button
                                type="button"
                                variant={field.value === 'budget' ? "default" : "outline"}
                                className={cn(
                                  "h-12 text-base font-semibold rounded-lg transition-all duration-200 border-2",
                                  field.value === 'budget'
                                    ? "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-blue-500 shadow-md"
                                    : "hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50/10 border-border"
                                )}
                                onClick={() => field.onChange('budget')}
                              >
                                <FileText className="mr-2 h-5 w-5" />
                                Orçamento
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Right Section - Action Buttons */}
                  <div className="flex items-center gap-3 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => onOpenChange(false)}
                      disabled={isSaving}
                      className="h-12 px-8 text-base font-medium"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      form={formId}
                      disabled={isSaving || (items.length === 0 && !isEditMode)}
                      size="lg"
                      className="h-12 px-8 text-base font-medium shadow-lg"
                    >
                      {isSaving ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="h-5 w-5 mr-2" />
                          {isEditMode ? 'Salvar Alterações' : `Criar ${formType === 'sale' ? 'Venda' : 'Orçamento'}`}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Form Content */}
              <div className="flex-1 overflow-hidden">
                <form id={formId} onSubmit={form.handleSubmit(onSubmit)} className="h-full">
                  <ScrollArea className="h-full px-6 py-4">
                    <div className="max-w-none space-y-4">
                {/* Card: Informações Básicas */}
                <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="client_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Cliente</FormLabel>
                            <div className="flex gap-2">
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione o cliente" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {clients.map((client) => (
                                    <SelectItem key={client.id} value={client.id}>
                                      {client.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => setShowNewClientDialog(true)}
                                className="shrink-0"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="sale_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Data da {formType === 'sale' ? 'Venda' : 'Orçamento'}</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="expiry_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Data de Validade</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} value={field.value || ''} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="cost_center_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Centro de Custo</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione (opcional)" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {costCenters.map((cc) => (
                                  <SelectItem key={cc.id} value={cc.id}>
                                    {cc.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="salesperson_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Vendedor</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione (opcional)" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {salesTeam.map((person) => (
                                  <SelectItem key={person.id} value={person.id}>
                                    {person.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Card: Itens */}
                <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Itens</h3>
                      <Button type="button" variant="outline" size="sm" onClick={addItem}>
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar Item
                      </Button>
                    </div>

                    {items.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        Nenhum item adicionado. Clique em "Adicionar Item" para começar.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {items.map((item, index) => (
                          <Card key={item.id} className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                              <div className="md:col-span-2">
                                <Label>Descrição</Label>
                                <Input
                                  value={item.description}
                                  onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                  placeholder="Descrição do item"
                                />
                              </div>
                              <div>
                                <Label>Quantidade</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.quantity}
                                  onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                />
                              </div>
                              <div>
                                <Label>Preço Unit.</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.unitPrice}
                                  onChange={(e) => updateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                />
                              </div>
                              <div>
                                <Label>Total</Label>
                                <div className="h-10 px-3 py-2 bg-muted rounded-md text-sm">
                                  R$ {calculateItemTotal(item).toFixed(2)}
                                </div>
                              </div>
                              <div className="flex items-end">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeItem(item.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Card: Resumo */}
                {items.length > 0 && (
                  <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold mb-4">Resumo dos Valores</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Subtotal:</span>
                          <span>R$ {calculateSubtotal().toFixed(2)}</span>
                        </div>
                        {calculateTotalDiscount() > 0 && (
                          <div className="flex justify-between text-red-600">
                            <span>Descontos:</span>
                            <span>-R$ {calculateTotalDiscount().toFixed(2)}</span>
                          </div>
                        )}
                        {calculateTotalTax() > 0 && (
                          <div className="flex justify-between">
                            <span>Impostos:</span>
                            <span>R$ {calculateTotalTax().toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-lg pt-2 border-t">
                          <span>Total:</span>
                          <span className="text-primary">R$ {calculateTotal().toFixed(2)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Card: Observações */}
                <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel>Observações</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Observações gerais..."
                                className="resize-none min-h-[80px] text-sm"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="internal_notes"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel>Observações Internas</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Observações internas..."
                                className="resize-none min-h-[80px] text-sm"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
                  </div>
                </ScrollArea>
              </form>
            </div>
          </div>
        </Form>
      </DialogContent>
    </Dialog>

    <NewClientDialog
      open={showNewClientDialog}
      onOpenChange={setShowNewClientDialog}
      onClientCreated={handleClientCreated}
    />
  </>
);
}
